/**
 * Recovery for "a lazily-loaded chunk failed to load" errors that surface after
 * a deploy: the user's cached index.html references content-hashed chunks that
 * no longer exist on the server, so the dynamic import fails and the route (or
 * the whole app) renders blank.
 *
 * Detection covers the legacy Webpack message *and* the esbuild/native
 * dynamic-import messages, plus the MIME/syntax errors that occur when a missing
 * chunk is rewritten to index.html (HTML served for a `.js` request — exactly
 * what our `** -> /index.html` Hosting rewrite does).
 */
export function isChunkLoadError(error: unknown): boolean {
  const parts: string[] = [];
  if (typeof error === 'string') {
    parts.push(error);
  } else if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    for (const key of ['message', 'stack', 'originalStack']) {
      if (typeof e[key] === 'string') parts.push(e[key] as string);
    }
  }
  const msg = parts.join(' ').toLowerCase();
  if (!msg) return false;
  return (
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to load module script') ||
    msg.includes('expected a javascript module script') ||
    msg.includes("unexpected token '<'")
  );
}

/**
 * Reloads once to pick up the freshly deployed index.html + chunks. Guarded with
 * a short window so a genuinely broken deploy can't trap the user in a reload
 * loop.
 */
export function reloadOnceForChunkError(): void {
  const KEY = 'crewdle:chunk-reload-at';
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? '0');
    if (Date.now() - last < 30_000) return; // already attempted very recently
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode/quota) — fall through and reload.
  }
  location.reload();
}

/**
 * Registers global listeners that recover from chunk-load failures originating
 * outside the router — dynamic imports in services, `@defer` views, etc.
 */
export function registerChunkErrorRecovery(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadOnceForChunkError();
  });
  window.addEventListener(
    'error',
    (event) => {
      if (isChunkLoadError(event.error ?? event.message)) reloadOnceForChunkError();
    },
    true, // capture: resource/module load errors don't bubble
  );
}
